/**
 * Shared building blocks for UILM key helpers. Private to `i18n/utils/` —
 * import the public `uilmKeyForX` helpers, not these directly.
 */

/**
 * Convert kebab-case / camelCase / snake_case / mixed input to UPPER_SNAKE_CASE.
 * Also strips characters that are illegal in a key segment.
 *
 *   toUpperSnake('employment-agreement')      → 'EMPLOYMENT_AGREEMENT'
 *   toUpperSnake('studentAgreement')          → 'STUDENT_AGREEMENT'
 *   toUpperSnake('Mergers & Acquisitions')    → 'MERGERS_ACQUISITIONS'
 */
export function toUpperSnake(input: string): string {
  return input
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // split camelCase boundaries
    .replace(/[-\s]+/g, '_') // hyphens / whitespace → underscore
    .replace(/[^A-Za-z0-9_]/g, '_') // strip special chars (&, /, (, ), …)
    .replace(/_+/g, '_') // collapse repeated underscores
    .replace(/^_|_$/g, '') // trim leading/trailing underscores
    .toUpperCase();
}

/**
 * Build a stable short identifier from a UDS ItemId (UUID) by taking the
 * first 8 hex characters and prefixing a letter. The letter (e.g. 'S' for
 * screens, 'F' for fields) ensures the segment starts with a letter, not
 * a digit, and visually separates the entity type from the hex.
 *
 *   shortItemId('a3f2b1c8-9d4e-4c1f-b0a1-9d4e4c1fb0a1', 'S')  →  'SA3F2B1C8'
 */
export function shortItemId(itemId: string, letterPrefix: string): string {
  const hex = itemId.replace(/-/g, '').slice(0, 8).toUpperCase();
  return `${letterPrefix}${hex}`;
}

/**
 * Hard UILM-side limit on key name length. The server rejects anything
 * longer, so any key builder that can produce variable-length output
 * (notably option keys) must clamp.
 */
export const MAX_UILM_KEY_LENGTH = 100;

/**
 * Max length for the slug portion of a key. Tuned so the longest fixed
 * suffix (`_SCR_S{8HEX}_FLD_F{8HEX}_PLACEHOLDER` = 40 chars) still fits
 * comfortably within `MAX_UILM_KEY_LENGTH`, and option keys still have
 * room for the option-value tail. Bumped via deliberate review only.
 */
export const MAX_SLUG_KEY_LENGTH = 50;

/**
 * Tiny deterministic hash for collision-avoiding short suffixes. FNV-1a
 * 32-bit — plenty of distribution for the handful of options on a
 * single field, no crypto library needed.
 *
 *   shortHash('CONFIDENTIAL_INFORMATION_INCLUDING_TRADE_SECRETS', 6)  →  '1A2B3C'
 */
export function shortHash(input: string, hexChars: number): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h.toString(16).toUpperCase().padStart(8, '0').slice(0, hexChars);
}

/**
 * If `keyWithFullValue` is within the UILM length limit, return it unchanged.
 * Otherwise truncate the trailing `value` segment and append a deterministic
 * short hash of the full value so two long-but-similar values produce
 * distinct keys.
 *
 *   prefix      = '..._OPT'   (no trailing underscore)
 *   value       = the normalized option value
 *   hashSuffixLen — defaults to 6 hex chars (`_H` + 6 = 8 chars overhead)
 *
 * Short values are unaffected — this preserves existing keys.
 */
export function clampToMaxLength(
  prefix: string,
  value: string,
  hashSuffixLen = 6,
): string {
  const fullKey = `${prefix}_${value}`;
  if (fullKey.length <= MAX_UILM_KEY_LENGTH) return fullKey;

  const hashSuffix = `_H${shortHash(value, hashSuffixLen)}`;
  // Reserve room for: prefix + '_' + truncated + hashSuffix
  const availableForValue = MAX_UILM_KEY_LENGTH - prefix.length - 1 - hashSuffix.length;
  // Trim any trailing underscores from the truncated head so we don't
  // emit `..._OPT_FOO__H1A2B3C` when the cut lands on a separator.
  const truncated = value.slice(0, Math.max(availableForValue, 0)).replace(/_+$/, '');
  return `${prefix}_${truncated}${hashSuffix}`;
}

/**
 * Normalize a template slug for use as the head of a UILM key. Short
 * slugs (≤ `MAX_SLUG_KEY_LENGTH` after UPPER_SNAKE_CASE) pass through
 * unchanged so existing keys keep their names. Longer slugs are
 * truncated and given a deterministic `_H{6hex}` suffix so two
 * pathological slugs sharing a long prefix produce distinct keys.
 *
 *   clampSlugForKey('nda')                         → 'NDA'
 *   clampSlugForKey('employment-agreement')        → 'EMPLOYMENT_AGREEMENT'
 *   clampSlugForKey('a-very-long-template-slug-that-keeps-going-and-going')
 *     → 'A_VERY_LONG_TEMPLATE_SLUG_THAT_KEEPS_GOIN_H1A2B3C'
 */
export function clampSlugForKey(rawSlug: string): string {
  const normalized = toUpperSnake(rawSlug);
  if (normalized.length <= MAX_SLUG_KEY_LENGTH) return normalized;
  const hashSuffix = `_H${shortHash(normalized, 6)}`;
  const truncated = normalized
    .slice(0, MAX_SLUG_KEY_LENGTH - hashSuffix.length)
    .replace(/_+$/, '');
  return `${truncated}${hashSuffix}`;
}
